/**
 * Thread notification coordinator (ticket 3531, upstream pin e3e7cc3f, MIT).
 * Adapted from `apps/web/src/components/ThreadNotificationCoordinator.tsx`:
 * upstream reads thread shells per environment from the environment shell
 * atoms, forge reads the sidebar summary map from the app store, and the
 * transition diff itself lives in `threadNotificationCoordinator.logic.ts`
 * so it can be tested pure. The badge, sound, desktop Notification and
 * toast flows are upstream's; forge's toast payload has no leading-icon
 * slot, so attention toasts rely on the type icon.
 *
 * @module ThreadNotificationCoordinator
 */
import { useNavigate, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useRef } from "react";

import { useSettings } from "~/hooks/useSettings";
import { useStore } from "../store";
import { selectSidebarSummariesAcrossEnvironments } from "../storeSelectors";
import type { NotificationMode } from "@cmmd-forge/contracts/settings";
import type { ThreadId } from "@cmmd-forge/contracts";
import {
  diffThreadNotifications,
  notificationTitleForEvent,
  toastTypeForEvent,
  type SidebarThreadLike,
  type ThreadNotificationEvent,
  type ThreadNotificationState,
} from "./threadNotificationCoordinator.logic";
import {
  hasDesktopNotifications,
  hasNotificationSound,
  playNotificationSound,
  setNotificationBadge,
  unlockNotificationAudio,
} from "../threadNotifications";
import { toastManager } from "./ui/toast";

export function ThreadNotificationCoordinator() {
  const summaries = useStore(selectSidebarSummariesAcrossEnvironments);
  const mode = useSettings((settings) => settings.notificationMode);
  const inAppNotificationsEnabled = useSettings((settings) => settings.inAppNotificationsEnabled);
  const pending = useRef(new Map<string, Notification>());
  const onNotification = useCallback((_environmentId: string, notification: Notification) => {
    if (notification.tag) pending.current.get(notification.tag)?.close();
    if (notification.tag) pending.current.set(notification.tag, notification);
    setNotificationBadge(pending.current.size);
  }, []);

  // A thread that leaves the sidebar map can no longer be focused from here;
  // retire its desktop notification so the badge tracks what is reachable.
  useEffect(() => {
    const liveTags = new Set(summaries.map((thread) => `${thread.environmentId}:${thread.id}`));
    for (const [tag, notification] of pending.current) {
      if (liveTags.has(tag)) continue;
      notification.close();
      pending.current.delete(tag);
    }
  }, [summaries]);

  useEffect(() => {
    const clear = () => {
      for (const notification of pending.current.values()) notification.close();
      pending.current.clear();
      setNotificationBadge(0);
    };
    clear();
    if (!hasDesktopNotifications(mode)) return;
    const unsubscribe = window.desktopBridge?.onNotificationBadgeClear?.(clear);
    window.addEventListener("focus", clear);
    return () => {
      unsubscribe?.();
      window.removeEventListener("focus", clear);
      clear();
    };
  }, [mode]);

  useEffect(() => {
    if (!hasNotificationSound(mode)) return;
    document.addEventListener("pointerdown", unlockNotificationAudio);
    document.addEventListener("keydown", unlockNotificationAudio);
    return () => {
      document.removeEventListener("pointerdown", unlockNotificationAudio);
      document.removeEventListener("keydown", unlockNotificationAudio);
    };
  }, [mode]);

  if (mode === "off" && !inAppNotificationsEnabled) return null;

  return (
    <EnvironmentThreadNotifications
      summaries={summaries}
      mode={mode}
      inAppNotificationsEnabled={inAppNotificationsEnabled}
      onNotification={onNotification}
    />
  );
}

function EnvironmentThreadNotifications({
  summaries,
  mode,
  inAppNotificationsEnabled,
  onNotification,
}: {
  summaries: ReadonlyArray<SidebarThreadLike>;
  mode: NotificationMode;
  inAppNotificationsEnabled: boolean;
  onNotification: (environmentId: string, notification: Notification) => void;
}) {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const previous = useRef(new Map<ThreadId, ThreadNotificationState>());
  // The sound's last-instant gate reads the freshest mode without re-rendering.
  const modeRef = useRef(mode);
  modeRef.current = mode;

  useEffect(() => {
    const { events, next } = diffThreadNotifications(previous.current, summaries);
    previous.current = next;
    if (events.length === 0) return;
    const threadById = new Map(summaries.map((thread) => [thread.id, thread]));
    const active = { environmentId: params.environmentId, threadId: params.threadId };
    for (const event of events) {
      const thread = threadById.get(event.threadId);
      if (!thread) continue;
      if (hasNotificationSound(mode)) {
        void playNotificationSound(event.kind, () => hasNotificationSound(modeRef.current));
      }
      if (shouldShowInAppToast(inAppNotificationsEnabled, active, thread)) {
        presentToast(event, thread, navigate);
        continue;
      }
      presentDesktopNotification(event, thread, mode, navigate, onNotification);
    }
  }, [inAppNotificationsEnabled, mode, navigate, onNotification, params, summaries]);

  return null;
}

type ActiveRouteThread = { environmentId: string | undefined; threadId: string | undefined };

function isViewingThread(active: ActiveRouteThread, thread: SidebarThreadLike): boolean {
  return active.environmentId === thread.environmentId && active.threadId === thread.id;
}

/** Toast only for a visible, focused tab that is not already on the thread. */
function shouldShowInAppToast(
  inAppNotificationsEnabled: boolean,
  active: ActiveRouteThread,
  thread: SidebarThreadLike,
): boolean {
  return (
    inAppNotificationsEnabled &&
    document.visibilityState === "visible" &&
    document.hasFocus() &&
    !isViewingThread(active, thread)
  );
}

/** Desktop presentation needs the mode, a hidden or unfocused tab, and permission. */
function mayPresentDesktop(mode: NotificationMode): boolean {
  return (
    hasDesktopNotifications(mode) &&
    !(document.visibilityState === "visible" && document.hasFocus()) &&
    typeof Notification !== "undefined" &&
    Notification.permission === "granted"
  );
}

type NavigateToThread = (input: {
  to: "/$environmentId/$threadId";
  params: { environmentId: string; threadId: string };
}) => unknown;

function presentToast(
  event: ThreadNotificationEvent,
  thread: SidebarThreadLike,
  navigate: NavigateToThread,
): void {
  const toastId = toastManager.add({
    type: toastTypeForEvent(event),
    title: notificationTitleForEvent(event),
    description: event.title,
    data: { hideCopyButton: true },
    actionProps: {
      children: "Open thread",
      onClick: () => {
        toastManager.close(toastId);
        void navigate({
          to: "/$environmentId/$threadId",
          params: { environmentId: thread.environmentId, threadId: thread.id },
        });
      },
    },
  });
}

function presentDesktopNotification(
  event: ThreadNotificationEvent,
  thread: SidebarThreadLike,
  mode: NotificationMode,
  navigate: NavigateToThread,
  onNotification: (environmentId: string, notification: Notification) => void,
): void {
  if (!mayPresentDesktop(mode)) return;
  try {
    const notification = new Notification(notificationTitleForEvent(event), {
      body: event.title,
      tag: `${thread.environmentId}:${thread.id}`,
      silent: true,
    });
    onNotification(thread.environmentId, notification);
    notification.addEventListener("click", () => {
      notification.close();
      window.focus();
      void navigate({
        to: "/$environmentId/$threadId",
        params: { environmentId: thread.environmentId, threadId: thread.id },
      });
    });
  } catch {
    // Some browsers expose Notification but reject desktop presentation.
  }
}
